// Dashboard.tsx
// Full corrected Dashboard component (TypeScript + React)
// Replace your existing Dashboard with this file.

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/core/Auth";
import { MostOrderDish } from "./dashbordcomponent/MostOrderedDish";
import { TopRatedDish } from "./dashbordcomponent/TopRatedDish";
import { RecentRating } from "./dashbordcomponent/RecentRating";
import { Graph } from "./dashbordcomponent/Graph";
import { RecentOreder } from "./dashbordcomponent/RecentOredr";
import { useEffect, useRef, useState } from "react";
import CouponBanner from "./dashbordcomponent/Coupon";
import socket from "../../component/socket";
import { normalizeWorkingHours } from "../../timingConfig";
import {
  listOrdersData,
  ProfileDetails,
  totalNetValue,
} from "../../auth/core/_requests";
import InsuranceModal from "./dashbordcomponent/InsurnceModal";

interface Order {
  orderstatusId: number;
  totalAmount: number;
}

const Dashboard = () => {
  const [listOrders, setListOrders] = useState<Order[] | undefined>();
  const [NetValue, setNetValue] = useState(0);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [insurance, setInsurance] = useState<boolean | undefined>(undefined);
  const [certificate, setCertificate] = useState<boolean | undefined>(
    undefined
  );
  const { currentUser } = useAuth();
  const date = new Date();
  const day: number = date.getDate();
  const month: string = date.toLocaleString("default", { month: "short" });
  const year: number = date.getFullYear();
  const currentDate: string = `${day} ${month} ${year}`;
  const navigate = useNavigate();
  const chefId = currentUser?.uuid;

  const handleModal = () => setShowModal((prevShowModal) => !prevShowModal);

  const [getIsReport, setIsReport] = useState<any>();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planMessage, setPlanMessage] = useState("");
  const [planAction, setPlanAction] = useState<(() => void) | null>(null);

  // Default free plan limits (fallback)
  const FREE_DISH_LIMIT = 10;
  const FREE_BANNER_LIMIT = 1;

  // Helper: get numeric safe value
  const safeNum = (v: any, fallback = 0) =>
    typeof v === "number" ? v : fallback;

  useEffect(() => {
    if (!chefId) return;

    ProfileDetails(chefId).then((res) => {
      const data = res.data;

      // Insurance check
      if (data.is_insurance === false || data.is_insurance === null) {
        setShowModal(true);
      }

      // Certificate check
      setInsurance(data.is_insurance);
      setCertificate(data.isCertifiedChef);

      // Report access check (plan-level)
      const chefPlanAccess =
        data?.chef_plan_subscriptions?.[0]?.plan?.chef_plan_accesses?.[0];
      if (chefPlanAccess) {
        setIsReport(chefPlanAccess.is_report);
      }

      // ---------- Determine limits ----------
      // Prefer plan-level chef_plan_accesses.*_count if present
      // Fallback to planDishCount / planBannerCount returned by backend
      // Finally fallback to FREE_* constants

      const planDishCountFromAccess = chefPlanAccess?.dish_count;
      const planBannerCountFromAccess = chefPlanAccess?.banner_count;

      const planDishCountFromRoot = data?.planDishCount;
      const planBannerCountFromRoot = data?.planBannerCount;

      const dishLimit =
        safeNum(planDishCountFromAccess, NaN) ||
        safeNum(planDishCountFromRoot, NaN) ||
        FREE_DISH_LIMIT;

      const bannerLimit =
        safeNum(planBannerCountFromAccess, NaN) ||
        safeNum(planBannerCountFromRoot, NaN) ||
        FREE_BANNER_LIMIT;

      // ---------- Manage Dishes modal ----------
      // totalDishCount is expected from ProfileDetails response (confirmed)
      const totalDishCount = safeNum(data?.totalDishCount, 0);

      if (totalDishCount > dishLimit) {
        setPlanMessage(
          `You currently have ${totalDishCount} dish${
            totalDishCount > 1 ? "es" : ""
          }. Your plan allows only ${dishLimit}. Please remove ${
            totalDishCount - dishLimit
          } dish${totalDishCount - dishLimit > 1 ? "es" : ""} before continuing.`
        );
        setPlanAction(() => () => navigate("/dish/list"));
        setShowPlanModal(true);
        return;
      }

      // ---------- Manage Banners modal ----------
      // bannerCount & planBannerCount are expected in the response
      const bannerCount = safeNum(data?.bannerCount, 0);

      if (bannerCount > bannerLimit) {
        setPlanMessage(
          `You currently have ${bannerCount} banner${
            bannerCount > 1 ? "s" : ""
          }. Your plan allows only ${bannerLimit}. Please remove ${
            bannerCount - bannerLimit
          } banner${bannerCount - bannerLimit > 1 ? "s" : ""} before continuing.`
        );
        setPlanAction(() => () => navigate("/settings/change-banner"));
        setShowPlanModal(true);
        return;
      }
    });
  }, [chefId, navigate]);

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    if (!chefId) return;
    listOrdersData(chefId).then((res) => {
      setListOrders(res);
    });
    totalNetValue(chefId).then((res: any) => {
      setNetValue(res?.totalNetValue);
    });
  }, [chefId]);

  const viewReport = () => {
    if (getIsReport) {
      navigate("/report/list");
    } else {
      navigate("/access");
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/${encodeURIComponent(searchTerm)}`);
  };

  const certificateCheck = certificate === false || certificate === null;
  const insuranceCheck = insurance === false || insurance === null;

  return (
    <>
      <section className="container-fluid">
        <CouponBanner />
        <div className="row">
          <section className="content-section">
            <section className="row main-content">
              <div className="title mb-3 md-3">
                <h5 className="mb-1 mid-gray fw-bold">
                  <small>Hello, Chef {currentUser?.name}!</small>
                </h5>
              </div>

              <div className="row mb-3">
                <div className="row mb-3">
                  {certificateCheck && (
                    <div className="col-xl-6 col-12 mb-3 mb-md-0 mt-3">
                      <a
                        href="https://www.frayo.net/partners#certification-partner"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <img
                          src="/assets/images/ceritificatedash.jpg"
                          alt="img-grid"
                          style={{
                            height: insuranceCheck ? "290px" : "auto",
                          }}
                          height="200px"
                          width="100%"
                        />
                      </a>
                    </div>
                  )}
                  {insuranceCheck && (
                    <div
                      className="col-xl-6 col-12 mb-3 mb-md-0 mt-3"
                      onClick={handleModal}
                    >
                      <a
                        href="https://get.fliprogram.com/flip_annual?partner=298d2b3b-dd49-4f72-8285-f24a17213152"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <img
                          src="/assets/images/insurance.jpg"
                          style={{
                            height: certificateCheck ? "290px" : "auto",
                          }}
                          alt="img-grid"
                          height="290px"
                          width="100%"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="col-12 dashboard-section">
                <div className="row mb-4">
                  <div className="col-xl-7 col-12">
                    <div className="row">
                      <div className="col-md-4 col-12 mb-3 mb-md-0">
                        <a href="report/list" className="text-link">
                          <div className="snippet-block anime blue">
                            <div className="ico-wrap">
                              <span className="material-symbols-outlined">
                                orders
                              </span>
                            </div>
                            <div className="count-cont">
                              <div className="count">
                                <h4 className="medium mb-1">
                                  {listOrders?.length}
                                  <i className="material-symbols-outlined up">
                                    trending_up
                                  </i>
                                </h4>
                              </div>
                              <p className="mb-0 mid-gray">
                                <span className="tiny">
                                  Total Orders Received
                                </span>
                              </p>
                            </div>
                          </div>
                        </a>
                      </div>
                      <div className="col-md-4 col-12 mb-3 mb-md-0">
                        <div className="snippet-block anime green">
                          <div className="ico-wrap">
                            <span className="material-symbols-outlined">
                              payments
                            </span>
                          </div>
                          <a href="report/list" className="text-link">
                            <div className="count-cont">
                              <div className="count">
                                <h4 className="medium mb-1">
                                  $ {NetValue.toFixed(2)}
                                  <i className="material-symbols-outlined up">
                                    trending_up
                                  </i>
                                </h4>
                              </div>
                              <p className="mb-0 mid-gray">
                                <span className="tiny">Total Sales</span>
                              </p>
                            </div>
                          </a>
                        </div>
                      </div>
                      <div className="col-md-4 col-12">
                        <a href="report/list" className="text-link">
                          <div className="snippet-block anime red">
                            <div className="ico-wrap">
                              <span className="material-symbols-outlined">
                                do_not_disturb_on
                              </span>
                            </div>
                            <div className="count-cont">
                              <div className="count">
                                <h4 className="medium mb-1">
                                  {listOrders?.filter(
                                    (order) => order?.orderstatusId === 7
                                  ).length}
                                  <i className="material-symbols-outlined fall">
                                    trending_down
                                  </i>
                                </h4>
                              </div>
                              <p className="mb-0 mid-gray">
                                <span className="tiny">
                                  Total Rejected Orders
                                </span>
                              </p>
                            </div>
                          </div>
                        </a>
                      </div>

                      <div className="col-12 mt-4">
                        <div className="snippet-block">
                          <div className="head">
                            <h6 className="mb-0 fw-bold">
                              Orders by last week
                            </h6>
                            <a
                              href="javascript:;"
                              className="text-link"
                              onClick={viewReport}
                            >
                              View Report
                              <i className="material-symbols-outlined fall">
                                arrow_forward
                              </i>
                            </a>
                          </div>
                          <Graph />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-5 col-12 mt-xl-0 mt-4 mb-2">
                    <RecentOreder />
                  </div>
                </div>
                <div className="row ">
                  <div className="col-xl-4 col-lg-6 col-12 mb-4">
                    <MostOrderDish />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-12 mb-4">
                    <TopRatedDish />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-12 mb-4">
                    <RecentRating />
                  </div>
                </div>
              </div>
              <InsuranceModal
                showModal={showModal}
                handleModal={handleModal}
                setInsurance={setInsurance}
              />
            </section>
          </section>
        </div>
        {showPlanModal && (
          <div className="modal show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Manage Items</h5>
                </div>
                <div className="modal-body">
                  <p>{planMessage}</p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate("/subscription/list")}
                  >
                    Upgrade Plan
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      planAction?.();
                      setShowPlanModal(false);
                    }}
                  >
                    Manage Items
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export { Dashboard };
